import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { fileApi } from "src/1/apis/file.api";
import { CModal } from "src/1/common/components/others";

import RichTextEditor from "./RichTextEditor";

/**
 * Định dạng dung lượng file (VD: 1.2 MB, 450 KB)
 */
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export default function CreatePostModal({
  isOpen,
  open = isOpen,
  onClose,
  onSubmit,
  postToEdit,
}) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isPin, setIsPin] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);

  const fileInputRef = useRef(null);
  const isEditing = Boolean(postToEdit);

  const resetForm = () => {
    setTitle("");
    setContent("");
    setIsPin(false);
    setAttachedFiles([]);
    setIsUploading(false);
    setIsDragging(false);
    setFormResetKey((k) => k + 1);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    const isModalOpen = Boolean(open ?? isOpen);
    if (isModalOpen) {
      if (postToEdit) {
        setTitle(postToEdit.title || "");
        setContent(postToEdit.content || "");
        setIsPin(Boolean(postToEdit.is_pin || postToEdit.isPin));
        setAttachedFiles(
          Array.isArray(postToEdit.files) ? postToEdit.files : [],
        );
      } else {
        resetForm();
      }
    } else {
      resetForm();
    }
  }, [postToEdit, isOpen, open]);

  // Xử lý chọn 1 hoặc nhiều file từ File Input
  const handleFilesSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    await uploadFiles(selectedFiles);

    // Reset input để có thể chọn lại cùng file nếu muốn
    e.target.value = "";
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Upload các file lên hệ thống và gom các đối tượng file trả về (chứa id)
  const uploadFiles = async (filesList) => {
    setIsUploading(true);
    const uploadedList = [];

    for (const file of filesList) {
      try {
        const res = await fileApi.upload(file);
        const uploadedData = res?.data?.data || res?.data;
        if (uploadedData) {
          uploadedList.push(uploadedData);
        }
      } catch (err) {
        toast.error(err?.message || `Không thể tải lên file: ${file.name}`);
      }
    }

    if (uploadedList.length > 0) {
      setAttachedFiles((prev) => [...prev, ...uploadedList]);
      toast.success(
        `Đã tải lên thành công ${uploadedList.length} file đính kèm`,
      );
    }

    setIsUploading(false);
  };

  // Xử lý Drag & Drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    if (droppedFiles.length === 0) return;

    await uploadFiles(droppedFiles);
  };

  // Xóa file đã đính kèm
  const handleRemoveFile = (indexToRemove) => {
    setAttachedFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    const hasContent =
      content.replace(/<[^>]*>/g, "").trim().length > 0 ||
      content.includes("<img") ||
      content.includes("<table");

    if (!title.trim() || !hasContent) {
      toast.warning("Vui lòng nhập đầy đủ tiêu đề và nội dung bài viết!");
      return;
    }

    // Lấy danh sách ID trả về từ các file đã upload
    const file_ids = attachedFiles
      .map((f) => (typeof f === "object" ? f.id : f))
      .filter((id) => id !== undefined && id !== null);

    try {
      setIsSubmitting(true);
      await onSubmit({
        title: title.trim(),
        content: content.trim(),
        is_pin: isPin,
        file_ids,
      });
      resetForm();
      onClose();
    } catch (error) {
      // Error handled by caller
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CModal
      open={Boolean(open ?? isOpen)}
      onClose={handleClose}
      title={isEditing ? "Chỉnh sửa bài viết" : "Đăng bài viết kho mới"}
      size="lg"
      submitLabel={isEditing ? "Lưu thay đổi" : "Phát hành bài viết"}
      loading={isSubmitting || isUploading}
      onSubmit={handleSubmit}
    >
      <form onSubmit={handleSubmit} className="!space-y-5">
        {/* Title */}
        <div>
          <label className="!block !text-sm sm:!text-base !font-bold !text-slate-800 !tracking-wide !mb-1.5">
            Tiêu đề bài viết <span className="!text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            placeholder="VD: Kế hoạch điều phối hàng hóa tuần này..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="!w-full !px-4 !py-2.5 !text-sm sm:!text-base !text-slate-900 !bg-white !rounded-xl !border !border-slate-300 focus:!outline-none focus:!border-blue-600 focus:!ring-2 focus:!ring-blue-100 !transition-all placeholder:!text-slate-400 placeholder:!font-normal"
          />
        </div>

        {/* Content Rich Text Editor */}
        <div>
          <label className="!block !text-sm sm:!text-base !font-bold !text-slate-800 !tracking-wide !mb-1.5">
            Nội dung bài viết <span className="!text-red-500">*</span>
          </label>
          <RichTextEditor
            key={formResetKey}
            value={content}
            onChange={setContent}
          />
        </div>

        {/* Upload file đính kèm */}
        <div>
          <div className="!flex !items-center !justify-between !mb-1.5">
            <label className="!block !text-sm sm:!text-base !font-bold !text-slate-800 !tracking-wide">
              File đính kèm ({attachedFiles.length})
            </label>
            <span className="!text-xs sm:!text-sm !font-medium !text-slate-500">
              Hỗ trợ tải lên nhiều file (hình ảnh, PDF, tài liệu, Excel...)
            </span>
          </div>

          {/* Hidden Input file */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFilesSelect}
            className="!hidden"
            id="post-attachments-input"
          />

          {/* Dropzone vùng kéo thả & click tải lên */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isUploading && fileInputRef.current?.click()}
            className={`!border-2 !border-dashed !rounded-xl !p-4 !text-center !transition-all !cursor-pointer ${
              isDragging
                ? "!border-blue-500 !bg-blue-50/70"
                : isUploading
                  ? "!border-slate-300 !bg-slate-50 !cursor-not-allowed"
                  : "!border-slate-300 hover:!border-blue-500 !bg-slate-50/60 hover:!bg-white"
            }`}
          >
            {isUploading ? (
              <div className="!flex !items-center !justify-center !gap-2.5 !py-3 !text-blue-600">
                <svg
                  className="!w-5 !h-5 !animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="!opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="!opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="!text-sm sm:!text-base !font-bold !text-blue-700">
                  Đang tải lên các file đính kèm...
                </span>
              </div>
            ) : (
              <div className="!flex !flex-col !items-center !gap-2 !py-2">
                <div className="!w-10 !h-10 !rounded-xl !bg-blue-100/70 !text-blue-700 !flex !items-center !justify-center">
                  <svg
                    className="!w-5 !h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <div className="!text-sm sm:!text-base !font-bold !text-slate-800">
                  Nhấn vào đây hoặc kéo thả để chọn 1 hoặc nhiều file đính kèm
                </div>
                <p className="!text-xs sm:!text-sm !font-medium !text-slate-500">
                  Hỗ trợ file PNG, JPG, PDF, DOCX, XLSX... (không giới hạn số
                  lượng)
                </p>
              </div>
            )}
          </div>

          {/* Danh sách các file đã upload */}
          {attachedFiles.length > 0 && (
            <div className="!mt-3 !space-y-2 !max-h-48 !overflow-y-auto !pr-1">
              {attachedFiles.map((file, idx) => {
                const fileName =
                  file.original_name ||
                  file.name ||
                  file.file_name ||
                  `File ${idx + 1}`;
                const fileSize = file.size ? formatFileSize(file.size) : null;
                const isImage =
                  file.type?.startsWith?.("image/") ||
                  /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
                const isPdf = /\.pdf$/i.test(fileName);
                const isExcel = /\.(xlsx|xls|csv)$/i.test(fileName);

                return (
                  <div
                    key={file.id || idx}
                    className="!flex !items-center !justify-between !gap-2.5 !p-2.5 !bg-slate-50 hover:!bg-slate-100 !border !border-slate-200 !rounded-xl !transition-colors"
                  >
                    <div className="!flex !items-center !gap-2.5 !min-w-0">
                      {/* Biểu tượng file hoặc ảnh thu nhỏ */}
                      {isImage && (file.path || file.url) ? (
                        <img
                          src={file.path || file.url}
                          alt=""
                          className="!w-8 !h-8 !rounded-lg !object-cover !shrink-0 !border !border-slate-200"
                        />
                      ) : (
                        <div
                          className={`!w-8 !h-8 !rounded-lg !flex !items-center !justify-center !shrink-0 !font-extrabold !text-xs ${
                            isPdf
                              ? "!bg-red-100 !text-red-700"
                              : isExcel
                                ? "!bg-emerald-100 !text-emerald-700"
                                : "!bg-blue-100 !text-blue-700"
                          }`}
                        >
                          {isPdf ? "PDF" : isExcel ? "XLS" : "FILE"}
                        </div>
                      )}

                      <div className="!min-w-0">
                        <p
                          className="!font-bold !text-sm !text-slate-800 !truncate !max-w-[420px]"
                          title={fileName}
                        >
                          {fileName}
                        </p>
                        {fileSize && (
                          <span className="!text-xs !font-medium !text-slate-500">
                            {fileSize}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(idx);
                      }}
                      className="!p-1.5 !text-slate-400 hover:!text-red-600 hover:!bg-red-50 !rounded-lg !transition-colors !shrink-0 !cursor-pointer"
                      title="Xóa file này"
                    >
                      <svg
                        className="!w-4 !h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pin Checkbox */}
        <div className="!bg-slate-50 !p-3.5 !rounded-xl !border !border-slate-200">
          <label className="!flex !items-center !gap-3 !cursor-pointer !text-sm sm:!text-base !font-bold !text-slate-800 !select-none">
            <input
              type="checkbox"
              checked={isPin}
              onChange={(e) => setIsPin(e.target.checked)}
              className="!w-4 !h-4 sm:!w-5 sm:!h-5 !text-[#053c7f] !rounded !border-slate-300 focus:!ring-blue-500 !cursor-pointer"
            />
            <span>📌 Ghim bài viết này lên đầu bảng tin kho</span>
          </label>
        </div>
      </form>
    </CModal>
  );
}
